import { supabase } from '../lib/supabase';

export type AuthUser = {
  id: string;
  email: string;
  role: string;
  name: string;
};

// Fetch the profile (role + display name) for an authenticated user id.
export const getProfile = async (userId: string): Promise<{ role: string; username: string | null } | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('role, username')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error in getProfile:', error);
    return null;
  }
};

// Build the app's AuthUser from a Supabase auth user + its profile.
export const buildAuthUser = async (
  authUser: { id: string; email?: string | null; user_metadata?: Record<string, unknown> }
): Promise<AuthUser> => {
  const profile = await getProfile(authUser.id);
  const metaName = (authUser.user_metadata?.name as string) || '';
  return {
    id: authUser.id,
    email: authUser.email ?? '',
    role: profile?.role ?? 'coach',
    name: profile?.username ?? metaName,
  };
};

// Sign in via Supabase Auth. Returns the AuthUser on success, null on failure.
export const signIn = async (email: string, password: string): Promise<AuthUser | null> => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.user) {
      if (error) console.error('Sign-in error:', error.message);
      return null;
    }
    return buildAuthUser(data.user);
  } catch (error) {
    console.error('Error in signIn:', error);
    return null;
  }
};

export const signOut = async (): Promise<void> => {
  await supabase.auth.signOut();
};
